import { ref, reactive, getCurrentInstance } from 'vue';
import store from '@/store';
import { useToast } from '@/composables/common/useToast';

/**
 * useEventVouchers
 * Composable that centralizes event-level voucher CRUD logic,
 * modal orchestration, form validation, and list filtering.
 *
 * @param {import('vue').Ref|string|number} eventoId — event ID (ref or raw value)
 * @returns {Object} public API
 */
export function useEventVouchers(eventoId) {
    const toast = useToast();
    const instance = getCurrentInstance();

    // Resolve event ID regardless of whether it is a ref or raw value
    const resolveEventoId = () => {
        if (eventoId && typeof eventoId === 'object' && 'value' in eventoId) {
            return eventoId.value;
        }
        return eventoId;
    };

    // ==================== STATE ====================

    const vouchers = ref([]);
    const loading = ref(false);
    const saving = ref(false);
    const selectedVoucher = ref(null);
    const apiError = ref(null);

    const filters = reactive({
        search: '',
        page: 1,
        per_page: 10,
    });

    const meta = reactive({
        current_page: 1,
        last_page: 1,
        total: 0,
    });

    // ==================== FORM VALIDATION ====================

    /**
     * Validates a voucher form object.
     * @param {{ name: string, code: string, discount_percent: *, discount_fixed: * }} form
     * @returns {Object|null} errors object if invalid, null if valid
     */
    function validateForm(form) {
        const errors = {};

        if (!form.name || (typeof form.name === 'string' && !form.name.trim())) {
            errors.name = 'El nombre es obligatorio';
        }

        if (!form.code || (typeof form.code === 'string' && !form.code.trim())) {
            errors.code = 'El código es obligatorio';
        }

        if (!form.units || Number(form.units) < 1) {
            errors.units = 'Las unidades son obligatorias';
        }

        const hasPercent = form.discount_percent !== null
            && form.discount_percent !== undefined
            && form.discount_percent !== ''
            && Number(form.discount_percent) !== 0;
        const hasFixed = form.discount_fixed !== null
            && form.discount_fixed !== undefined
            && form.discount_fixed !== ''
            && Number(form.discount_fixed) !== 0;

        if (hasPercent && hasFixed) {
            errors.discount = 'Solo se permite un tipo de descuento (porcentaje o fijo)';
        }

        if (!hasPercent && !hasFixed) {
            errors.discount = 'Debe indicar al menos un descuento (% o fijo)';
        }

        return Object.keys(errors).length > 0 ? errors : null;
    }

    // ==================== HELPERS ====================

    function notifySuccess(message) {
        if (toast && toast.success) {
            toast.success(message);
        } else if (instance?.proxy?.$toast) {
            instance.proxy.$toast.success(message);
        }
    }

    function notifyError(message) {
        if (toast && toast.error) {
            toast.error(message);
        } else if (instance?.proxy?.$toast) {
            instance.proxy.$toast.error(message);
        }
    }

    // ==================== CRUD OPERATIONS ====================

    /**
     * Fetches vouchers for the current event, respecting filters.
     */
    async function fetchVouchers() {
        loading.value = true;

        const filterParams = {};
        if (filters.search) {
            filterParams.search = filters.search;
        }
        if (filters.page) {
            filterParams.page = filters.page;
        }
        if (filters.per_page) {
            filterParams.per_page = filters.per_page;
        }

        const params = {
            filters: filterParams,
            evento_id: resolveEventoId(),
        };

        try {
            const res = await store.dispatch('BACK_EVENTS_DESCUENTOS_INDEX', params);

            if (Array.isArray(res.data)) {
                vouchers.value = res.data;
                meta.current_page = 1;
                meta.last_page = 1;
                meta.total = res.data.length;
            } else {
                vouchers.value = res.data.data || [];
                meta.current_page = res.data.meta?.current_page || 1;
                meta.last_page = res.data.meta?.last_page || 1;
                meta.total = res.data.meta?.total || 0;
            }
        } catch (error) {
            console.error('Error fetching event vouchers:', error);
            const payload = error.response?.data?.message || 'Error al cargar los descuentos';
            notifyError(payload);
        } finally {
            loading.value = false;
        }
    }

    /**
     * Creates a new voucher.
     * @param {{ name: string, code: string, units: number, discount_percent?: number, discount_fixed?: number }} form
     */
    async function createVoucher(form) {
        apiError.value = null;
        saving.value = true;
        const payload = {
            evento_id: resolveEventoId(),
            voucher: {
                name: form.name,
                code: form.code,
                units: form.units,
                ...(form.discount_percent ? { discount_percent: form.discount_percent } : {}),
                ...(form.discount_fixed ? { discount_fixed: form.discount_fixed } : {}),
            },
        };

        try {
            const res = await store.dispatch('BACK_EVENTS_DESCUENTOS_CREATE', payload);
            notifySuccess(res.data.message || 'Descuento creado correctamente');
            await fetchVouchers();
            return true;
        } catch (error) {
            const errorMsg = error.response?.data?.message || 'Error al crear el descuento';
            apiError.value = errorMsg;
            notifyError(errorMsg);
            return false;
        } finally {
            saving.value = false;
        }
    }

    /**
     * Updates an existing voucher with partial data.
     * @param {number|string} id — voucher ID
     * @param {{ name?: string, code?: string, units?: number, discount_percent?: number, discount_fixed?: number }} form
     */
    async function updateVoucher(id, form) {
        apiError.value = null;
        saving.value = true;
        const voucher = {};

        if (form.name !== undefined) voucher.name = form.name;
        if (form.code !== undefined) voucher.code = form.code;
        if (form.units !== undefined) voucher.units = form.units;
        if (form.discount_percent !== undefined) voucher.discount_percent = form.discount_percent;
        if (form.discount_fixed !== undefined) voucher.discount_fixed = form.discount_fixed;

        const params = {
            evento_id: resolveEventoId(),
            voucher_id: id,
            voucher,
        };

        try {
            const res = await store.dispatch('BACK_EVENTS_DESCUENTOS_UPDATE', params);
            notifySuccess(res.data.message || 'Descuento actualizado correctamente');
            await fetchVouchers();
            return true;
        } catch (error) {
            const errorMsg = error.response?.data?.message || 'Error al actualizar el descuento';
            apiError.value = errorMsg;
            notifyError(errorMsg);
            return false;
        } finally {
            saving.value = false;
        }
    }

    /**
     * Deletes a voucher.
     * @param {number|string} id — voucher ID
     */
    async function deleteVoucher(id) {
        apiError.value = null;
        saving.value = true;
        const params = {
            evento_id: resolveEventoId(),
            voucher_id: id,
        };

        try {
            const res = await store.dispatch('BACK_EVENTS_DESCUENTOS_DELETE', params);
            notifySuccess(res.data.message || 'Descuento eliminado correctamente');
            await fetchVouchers();
            return true;
        } catch (error) {
            const errorMsg = error.response?.data?.message || 'Error al eliminar el descuento';
            notifyError(errorMsg);
            return false;
        } finally {
            saving.value = false;
        }
    }

    // ==================== MODAL HELPERS ====================

    function closeModal(name) {
        if (instance?.proxy?.$modal) {
            instance.proxy.$modal.hide(name);
        }
        selectedVoucher.value = null;
    }

    // ==================== PUBLIC API ====================

    return {
        vouchers,
        loading,
        saving,
        selectedVoucher,
        filters,
        meta,
        apiError,
        validateForm,
        fetchVouchers,
        createVoucher,
        updateVoucher,
        deleteVoucher,
        closeModal,
    };
}
